import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../config/api';
import AuthenticatedNavbar from '../headers/AuthenticatedNavbar';

const API_URL = getApiBaseUrl();

const ACTION_LABELS = {
  post_like: 'Liked a post',
  post_unlike: 'Unliked a post',
  reply_like: 'Liked a comment',
  reply_unlike: 'Unliked a comment',
  post_comment: 'Commented on a post',
  reply_to_comment: 'Replied to a comment',
  user_login: 'Logged in',
  user_register: 'Created an account',
  post_share: 'Shared a post',
  post_repost: 'Reposted a post',
  post_unrepost: 'Removed a repost',
  post_create: 'Created a post',
  hiring_post_create: 'Posted a job',
  looking_for_job_create: 'Posted open to work',
  article_create: 'Published an article',
  job_application: 'Applied for a job',
};

const ACTION_BREAKDOWN_LABELS = {
  post_like: 'Post likes',
  post_unlike: 'Post unlikes',
  reply_like: 'Comment likes',
  reply_unlike: 'Comment unlikes',
  post_comment: 'Comments on posts',
  reply_to_comment: 'Replies to comments',
  user_login: 'User logins',
  user_register: 'New registrations',
  post_share: 'Post shares',
  post_repost: 'Post reposts',
  post_unrepost: 'Repost removals',
  post_create: 'Posts created',
  hiring_post_create: 'Hiring posts',
  looking_for_job_create: 'Open-to-work posts',
  article_create: 'Articles published',
  job_application: 'Job applications',
};

const PAGE_LABELS = {
  '/': 'Landing page',
  '/home': 'Home feed',
  '/login': 'Login',
  '/register': 'Register',
  '/admin': 'Admin panel',
  '/analytics': 'Analytics',
  '/assistant': 'COOL Assistant',
  '/news': 'News',
  '/events': 'Events',
  '/network': 'Network',
  '/usermanagement': 'User management',
  '/hiringmanagement': 'Hiring management',
  '/admin/groups': 'Admin groups',
  '/myprofileinfo': 'My profile',
  '/groups/groupinpage': 'Groups',
  '/my-applications': 'My applications',
  '/aboutus': 'About us',
};

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    total_views: 0,
    unique_visitors: 0,
    guest_visits: 0,
    total_interactions: 0,
    today_views: 0,
    today_interactions: 0,
    online_now: 0,
    page_views_by_page: [],
    interactions_by_action: [],
    recent_activity: [],
    ai: emptyAiMetrics(),
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.typeofuser === 'admin';

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    setError('');

    if (!API_URL) {
      setError('API URL is not configured');
      setLoading(false);
      return;
    }

    try {
      const qs = user?.id ? `?user_id=${encodeURIComponent(user.id)}` : '';
      const response = await fetch(`${API_URL}/get_page_metrics.php${qs}`);
      const data = await response.json();

      if (data.success) {
        setMetrics({
          total_views: data.metrics?.total_views || 0,
          unique_visitors: data.metrics?.unique_visitors || 0,
          guest_visits: data.metrics?.guest_visits || 0,
          total_interactions: data.metrics?.total_interactions || 0,
          today_views: data.metrics?.today_views || 0,
          today_interactions: data.metrics?.today_interactions || 0,
          online_now: data.metrics?.online_now || 0,
          page_views_by_page: data.metrics?.page_views_by_page || [],
          interactions_by_action: data.metrics?.interactions_by_action || [],
          recent_activity: data.metrics?.recent_activity || [],
          ai: {
            ...emptyAiMetrics(),
            ...(data.metrics?.ai || {}),
          },
        });
        setLastUpdated(new Date());
      } else {
        setError(data.message || 'Failed to load metrics');
      }
    } catch (err) {
      setError('Unable to reach the analytics service. Please check your connection or try again later.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/home', { replace: true });
      return;
    }
    loadMetrics();
    const timer = setInterval(loadMetrics, 30000);
    return () => clearInterval(timer);
  }, [isAdmin, loadMetrics, navigate]);

  if (!isAdmin) {
    return null;
  }

  const renderList = (items, labelKey, valueKey, labelFormatter) => {
    if (!items.length) {
      return (
        <p style={{ color: '#555', margin: 0 }}>
          No data yet. Browse the site, like posts, or leave comments to start collecting metrics.
        </p>
      );
    }

    const denom = metricSum(items, valueKey);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {items.map((item) => {
          const value = Number(item[valueKey]) || 0;
          const ratio = denom > 0 ? (value / denom) * 100 : 0;
          const label = labelFormatter ? labelFormatter(item[labelKey]) : groupLabel(item[labelKey]);
          return (
            <div key={item[labelKey]} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '0.95rem', color: '#1f2a44', fontWeight: 600 }}>{label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    flexGrow: 1,
                    height: '10px',
                    borderRadius: '999px',
                    background: '#e0e4ff',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(5, Math.min(100, ratio))}%`,
                      height: '100%',
                      borderRadius: '999px',
                      background: '#1d4ed8',
                    }}
                  />
                </div>
                <span style={{ fontSize: '0.85rem', color: '#334155', minWidth: '70px', textAlign: 'right' }}>
                  {value.toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const statCards = [
    { label: 'Site visits', value: metrics.total_views, hint: 'Every page load across the site' },
    { label: 'Unique visitors', value: metrics.unique_visitors, hint: 'Logged-in users who visited' },
    { label: 'Guest visits', value: metrics.guest_visits, hint: 'Visits without logging in' },
    { label: 'Total interactions', value: metrics.total_interactions, hint: 'Likes, comments, logins, and more' },
    { label: 'Today’s views', value: metrics.today_views, hint: 'Page views since midnight' },
    { label: 'Online now', value: metrics.online_now, hint: 'Active in the last minute' },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f6ff' }}>
      <AuthenticatedNavbar user={user} onLogout={onLogout} />

      <div
        style={{
          padding: '14px 20px',
          background: '#eef2ff',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.9rem' }}>Admin analytics</p>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#0f172a' }}>Page views & interactions</h2>
          <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem' }}>
            {user?.first_name ? `Hi ${user.first_name}` : 'Monitor site activity in real time.'}
            {lastUpdated ? ` · Updated ${formatRelativeTime(lastUpdated.toISOString())}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            style={{
              border: '1px solid #cbd5f5',
              borderRadius: '999px',
              background: '#fff',
              color: '#1d4ed8',
              padding: '8px 18px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Back to posts
          </button>
          <button
            type="button"
            onClick={loadMetrics}
            disabled={loading}
            style={{
              border: 'none',
              borderRadius: '999px',
              background: '#1d4ed8',
              color: '#fff',
              padding: '8px 18px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            Refresh metrics
          </button>
        </div>
      </div>

      <main style={{ padding: '24px 32px 40px', display: 'flex', flexDirection: 'column', gap: '28px' }}>
        {error && (
          <div style={{ padding: '12px 16px', background: '#fee2e2', borderRadius: '12px', color: '#b91c1c' }}>
            {error}
          </div>
        )}

        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
          }}
        >
          {statCards.map((card) => (
            <div
              key={card.label}
              style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '18px',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              }}
            >
              <p style={{ margin: '0 0 6px', fontSize: '0.8rem', color: '#64748b' }}>{card.label}</p>
              <h3 style={{ margin: '0 0 4px', fontSize: '2rem', color: '#0f172a' }}>
                {loading && !lastUpdated ? '…' : card.value.toLocaleString()}
              </h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>{card.hint}</p>
            </div>
          ))}
        </section>

        <section
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>AI usage and cost</h4>
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              Assistant chats and Help me write requests. Cost is estimated from published token prices and can differ from the provider invoice.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '12px',
            }}
          >
            {[
              { label: 'AI requests', value: metrics.ai.requests.toLocaleString(), hint: `${metrics.ai.successful.toLocaleString()} succeeded · ${metrics.ai.failed.toLocaleString()} failed` },
              { label: 'Today’s requests', value: metrics.ai.today_requests.toLocaleString(), hint: `${metrics.ai.today_successful.toLocaleString()} succeeded today` },
              { label: 'Estimated cost', value: formatUsd(metrics.ai.total_cost), hint: 'All recorded requests' },
              { label: 'Today’s cost', value: formatUsd(metrics.ai.today_cost), hint: 'Since midnight' },
              { label: 'Input tokens', value: metrics.ai.input_tokens.toLocaleString(), hint: `${metrics.ai.today_input_tokens.toLocaleString()} today` },
              { label: 'Output tokens', value: metrics.ai.output_tokens.toLocaleString(), hint: `${metrics.ai.today_output_tokens.toLocaleString()} today` },
            ].map((card) => (
              <div
                key={card.label}
                style={{
                  background: '#f8fafc',
                  borderRadius: '12px',
                  padding: '14px',
                }}
              >
                <p style={{ margin: '0 0 6px', fontSize: '0.75rem', color: '#64748b' }}>{card.label}</p>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.45rem', color: '#0f172a' }}>
                  {loading && !lastUpdated ? '…' : card.value}
                </h3>
                <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>{card.hint}</p>
              </div>
            ))}
          </div>

          {(metrics.ai.unpriced_requests > 0 || metrics.ai.estimated_requests > 0) && (
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#92400e' }}>
              {metrics.ai.estimated_requests > 0
                ? `${metrics.ai.estimated_requests.toLocaleString()} request${metrics.ai.estimated_requests === 1 ? '' : 's'} used an estimated token count because the provider did not return one. `
                : ''}
              {metrics.ai.unpriced_requests > 0
                ? `${metrics.ai.unpriced_requests.toLocaleString()} successful request${metrics.ai.unpriced_requests === 1 ? '' : 's'} used a model without a stored price, so that cost is shown as $0.`
                : ''}
            </p>
          )}

          {loading && !lastUpdated ? (
            <p style={{ color: '#64748b', margin: 0 }}>Collecting AI usage...</p>
          ) : metrics.ai.requests === 0 ? (
            <p style={{ color: '#555', margin: 0 }}>
              No AI requests yet. Usage appears after someone uses the assistant or Help me write.
            </p>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '20px',
              }}
            >
              <div>
                <h5 style={{ margin: '0 0 10px', fontSize: '0.95rem', color: '#0f172a' }}>By feature</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {metrics.ai.by_use.map((item) => (
                    <div key={`${item.feature}-${item.kind}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 650, color: '#1f2a44' }}>
                          {formatAiUse(item.feature, item.kind)}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: '#334155' }}>{formatUsd(item.cost)}</span>
                      </div>
                      <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                        {Number(item.requests).toLocaleString()} requests · {(Number(item.input_tokens) + Number(item.output_tokens)).toLocaleString()} tokens
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h5 style={{ margin: '0 0 10px', fontSize: '0.95rem', color: '#0f172a' }}>By model</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {metrics.ai.by_model.map((item) => (
                    <div key={`${item.provider}-${item.model}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 650, color: '#1f2a44' }}>
                          {item.model || 'Unknown model'}
                        </span>
                        <span style={{ fontSize: '0.85rem', color: '#334155' }}>{formatUsd(item.cost)}</span>
                      </div>
                      <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                        {formatProvider(item.provider)} · {Number(item.requests).toLocaleString()} requests · {(Number(item.input_tokens) + Number(item.output_tokens)).toLocaleString()} tokens
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {metrics.ai.recent.length > 0 && (
            <div>
              <h5 style={{ margin: '0 0 8px', fontSize: '0.95rem', color: '#0f172a' }}>Recent AI requests</h5>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {metrics.ai.recent.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '10px 0',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, color: '#0f172a', fontSize: '0.9rem' }}>
                        <strong>{item.username || 'Guest'}</strong>
                        {' · '}
                        {formatAiUse(item.feature, item.kind)}
                        {!item.success ? ' · failed' : ''}
                      </p>
                      <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '0.75rem' }}>
                        {item.model || 'Unknown model'} · {Number(item.input_tokens).toLocaleString()} in / {Number(item.output_tokens).toLocaleString()} out
                      </p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ color: '#0f172a', fontSize: '0.85rem', fontWeight: 650 }}>{formatUsd(item.cost)}</div>
                      <div style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{formatRelativeTime(item.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Top viewed pages</h4>
              {loading && <span style={{ fontSize: '0.85rem', color: '#475569' }}>Loading...</span>}
            </div>
            {loading && !lastUpdated ? (
              <p style={{ color: '#64748b' }}>Collecting data...</p>
            ) : (
              renderList(metrics.page_views_by_page, 'page', 'total_views', formatPageLabel)
            )}
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
            }}
          >
            <h4 style={{ margin: '0 0 14px', fontSize: '1.1rem', color: '#0f172a' }}>Interactions breakdown</h4>
            {loading && !lastUpdated ? (
              <p style={{ color: '#64748b' }}>Collecting interaction data...</p>
            ) : (
              renderList(metrics.interactions_by_action, 'action', 'total_actions', formatBreakdownLabel)
            )}
          </div>
        </section>

        <section
          style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Recent activity</h4>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Latest likes, comments, logins, posts, and applications
                {metrics.today_interactions > 0 ? ` · ${metrics.today_interactions} today` : ''}
              </p>
            </div>
          </div>

          {loading && !lastUpdated ? (
            <p style={{ color: '#64748b' }}>Loading activity...</p>
          ) : !metrics.recent_activity.length ? (
            <p style={{ color: '#555', margin: 0 }}>
              No interactions yet. When users like, comment, log in, or apply for jobs, they will show up here.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {metrics.recent_activity.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '12px 0',
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px', minWidth: 0 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: '#e0e7ff',
                        color: '#1d4ed8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        flexShrink: 0,
                      }}
                    >
                      {(item.username || 'G').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, color: '#0f172a', fontSize: '0.95rem' }}>
                        <strong>{item.username || 'Guest'}</strong>
                        {' · '}
                        {formatActionLabel(item.action)}
                      </p>
                      {item.page ? (
                        <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '0.8rem' }}>
                          {formatPageLabel(item.page)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <span style={{ color: '#94a3b8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                    {formatRelativeTime(item.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function emptyAiMetrics() {
  return {
    requests: 0,
    successful: 0,
    failed: 0,
    today_requests: 0,
    today_successful: 0,
    input_tokens: 0,
    output_tokens: 0,
    today_input_tokens: 0,
    today_output_tokens: 0,
    total_cost: 0,
    today_cost: 0,
    unpriced_requests: 0,
    estimated_requests: 0,
    by_use: [],
    by_model: [],
    recent: [],
  };
}

const AI_USE_LABELS = {
  'assistant:chat': 'COOL Assistant',
  'writing:post': 'Post captions',
  'writing:blog': 'Blogs',
  'writing:article': 'Articles',
  'writing:hiring': 'Hiring posts',
  'writing:open_to_work': 'Open to work',
  'writing:reply': 'Replies',
};

function formatAiUse(feature, kind) {
  const key = `${feature || ''}:${kind || ''}`;
  if (AI_USE_LABELS[key]) return AI_USE_LABELS[key];
  const featureLabel = feature === 'writing' ? 'Writing' : feature === 'assistant' ? 'Assistant' : feature || 'AI';
  const kindLabel = kind ? kind.replace(/_/g, ' ') : '';
  return kindLabel ? `${featureLabel} · ${kindLabel}` : featureLabel;
}

function formatProvider(provider) {
  const key = String(provider || '').toLowerCase();
  if (key === 'openai') return 'OpenAI';
  if (key === 'groq') return 'Groq';
  if (key === 'gemini' || key === 'google') return 'Gemini';
  return provider || 'Unknown provider';
}

function formatUsd(value) {
  const amount = Number(value) || 0;
  const digits = amount !== 0 && Math.abs(amount) < 0.01 ? 4 : 2;
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function metricSum(items, key) {
  return items.reduce((sum, item) => sum + (item[key] || 0), 0);
}

function groupLabel(value) {
  if (!value) return 'Unknown';
  return value.length > 40 ? `${value.slice(0, 40)}...` : value;
}

function formatPageLabel(page) {
  if (!page) return 'Unknown page';
  if (PAGE_LABELS[page]) return PAGE_LABELS[page];
  if (page.startsWith('/home/post/')) return `Post detail (#${page.replace('/home/post/', '')})`;
  if (page.startsWith('/home/hiring/')) return `Hiring post (#${page.replace('/home/hiring/', '')})`;
  if (page.startsWith('/home/public/')) return `Public post (#${page.replace('/home/public/', '')})`;
  if (page.startsWith('/group/')) return `Group (#${page.replace('/group/', '')})`;
  if (page.startsWith('/groups/')) return `Group (#${page.replace('/groups/', '')})`;
  if (page.startsWith('/news/')) return `News (#${page.replace('/news/', '')})`;
  if (page.startsWith('/events/')) return `Event (#${page.replace('/events/', '')})`;
  if (page.startsWith('/profile/')) return `Profile (#${page.replace('/profile/', '')})`;
  if (page.startsWith('/network')) {
    const tab = page.includes('tab=') ? page.split('tab=')[1] : '';
    return tab ? `Network · ${tab}` : 'Network';
  }
  if (page.startsWith('/hiring/applications')) return 'Hiring applications';
  return groupLabel(page);
}

function formatActionLabel(action) {
  if (!action) return 'Unknown activity';
  return ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatBreakdownLabel(action) {
  if (!action) return 'Unknown';
  return (
    ACTION_BREAKDOWN_LABELS[action] ||
    action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return dateStr;

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}
